import React, { useEffect, useState } from "react";
import { Student } from "@/types/models";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import StudentFormFields from "./form/StudentFormFields";
import { useStudentForm } from "@/hooks/useStudentForm";
import { useLocations } from "@/hooks/useLocations";
import { supabase } from "@/lib/supabase";

interface EditStudentDialogProps {
  student: Student;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdated: (student: Student) => void;
}

interface ParentWithAddress {
  id: string;
  first_name: string;
  last_name: string;
  address_street?: string;
  address_city?: string;
  address_state?: string;
  address_zip?: string;
}

const EditStudentDialog: React.FC<EditStudentDialogProps> = ({
  student,
  open,
  onOpenChange,
  onUpdated,
}) => {
  const { data: locations } = useLocations();
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [familyParents, setFamilyParents] = useState<ParentWithAddress[]>([]);
  
  useEffect(() => {
    console.log("EditStudentDialog rendered:", 
                "\n- Open state:", open, 
                "\n- Student:", student?.id);
  }, [open, student]);

  // Fetch parents for the family when dialog opens
  useEffect(() => {
    if (!open || !student?.family_id) return;
    
    const fetchParents = async () => {
      try {
        // First try with just basic fields to ensure it works
        const { data, error } = await supabase
          .from('parents')
          .select('id, first_name, last_name')
          .eq('family_id', student.family_id);
          
        if (error) throw error;
        console.log('Fetched parents for Same as Parent feature:', data);
        setFamilyParents(data || []);
      } catch (err) {
        console.error('Error fetching parents:', err);
      }
    };
    
    fetchParents();
  }, [open, student?.family_id]);

  const {
    formData,
    isSubmitting,
    handleChange,
    handleGenderChange,
    handleSwitchChange,
    handleImageUpload,
    handleEmergencyContactChange,
    saveStudent
  } = useStudentForm({ student, onUpdated });

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveStudent();
  };
  
  const handleParentAddressChange = (parentId: string | null) => {
    // Create a synthetic event
    const syntheticEvent = {
      target: {
        name: 'parent_id_for_address',
        value: parentId || ''
      }
    } as React.ChangeEvent<HTMLInputElement>;
    
    handleChange(syntheticEvent);
  };

  // Ensure we don't render anything if student is null
  if (!student) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] p-0 overflow-hidden flex flex-col">
        <DialogHeader className="px-6 py-4 border-b bg-gradient-to-r from-blue-50 to-purple-50 flex-shrink-0">
          <DialogTitle className="text-xl font-semibold text-gray-900">Edit Student</DialogTitle>
          <p className="text-sm text-muted-foreground">
            {`${student.first_name} ${student.last_name}`}
          </p>
        </DialogHeader>
        
        <div className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <form id="edit-student-form" onSubmit={handleFormSubmit}>
              <StudentFormFields
                formData={formData}
                handleChange={handleChange}
                handleGenderChange={handleGenderChange}
                handleSwitchChange={handleSwitchChange}
                handleImageUpload={handleImageUpload}
                locations={locations?.data}
                familyParents={familyParents}
                handleParentAddressChange={handleParentAddressChange}
                handleEmergencyContactChange={handleEmergencyContactChange}
                handlePickupAuthorizationChange={(authorizations) => {
                  // For now, we'll just log this - you'll need to wire it up to your form handling
                  console.log('Pickup authorizations changed:', authorizations);
                }}
              />
            </form>
          </div>
          
          <div className="px-6 py-4 border-t bg-white/80 backdrop-blur-sm flex justify-end gap-3 flex-shrink-0 sticky bottom-0">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              form="edit-student-form" 
              type="submit" 
              disabled={isSubmitting}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving Changes...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditStudentDialog;
